<template>
  <div class="technical-details-table-fi">
    <table
      v-if="attributes || displayAttributes"
      class="technical-details-table-fi__table"
    >
      <tbody>
        <tr
          v-for="(attribute, attributeIndex) in displayAttributes"
          :key="attributeIndex"
          class="technical-details-table-fi__table-row"
        >
          <th
            scope="row"
            class="technical-details-table-fi__table-column--left"
          >
            <template v-if="attribute.AttributeName">
              <div class="technical-details-table-fi__attribute-name">
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="attribute.AttributeName" />

                <icon-fi
                  v-if="isSpecialAttributeName(attribute)"
                  class="technical-details-table-fi__attribute-icon"
                  icon="screw"
                />
                <span
                  v-else-if="!technicalNamesDisabled && attribute.TechnicalName"
                  class="technical-details-table-fi__technical-name"
                  v-html="`(${attribute.TechnicalName})`"
                />
                <!-- eslint-disable vue/no-v-html -->
              </div>
            </template>
          </th>

          <td class="technical-details-table-fi__table-column--right">
            <span
              v-if="attribute.ValueList?.length"
              class="technical-details-table-fi__attribute-value"
            >
              {{ attribute.ValueList.join(', ') }}
            </span>
            <span
              v-else-if="!attribute.Value"
              class="technical-details-table-fi__attribute-value--dash"
            >
              &#8212;
            </span>
            <span
              v-else-if="attribute.Value === 'relevant'"
              class="technical-details-table-fi__attribute-value--check"
            >
              <icon-fi
                class="technical-details-table-fi__attribute-value-icon"
                icon="check"
              />
            </span>
            <!-- eslint-disable vue/no-v-html -->
            <span
              v-else
              class="technical-details-table-fi__attribute-value"
              v-html="attribute.Value"
            />
            <span
              v-if="attribute.Unit"
              v-html="` ${getBlankUnit(attribute.Unit)}`"
            />
            <!-- eslint-enable vue/no-v-html -->
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { computed } from 'vue';

import type { CatalogProductData } from '@/components/shop-modules/product-list-view/product-content-fi/ProductContentFi.vue';
import type { GetCatalogProductDataByIdQuery } from '@/generated/graphql';

type CatalogProductDataById = NonNullable<
  GetCatalogProductDataByIdQuery['getCatalogProductDataById']
>;

defineOptions({ name: 'TechnicalDetailsTableFi' });

const props = withDefaults(
  defineProps<{
    item?: CatalogProductData | { catalogProductData: CatalogProductDataById } | null;
    attributes?: any[] | null;
    technicalNamesDisabled?: boolean;
    source?: string | null;
  }>(),
  {
    item: () => ({}),
    attributes: null,
    technicalNamesDisabled: false,
    source: null,
  },
);

const displayAttributes = computed(() => {
  if (props.attributes) return props.attributes;
  const catalogProductData = props.item?.catalogProductData;
  if (!catalogProductData) {
    return [];
  }

  if (props.source === 'PerformanceDataAttributes') {
    return (catalogProductData as CatalogProductDataById).PerformanceDataAttributes || [];
  }

  return [
    ...(catalogProductData.Attributes || []),
    ...(catalogProductData.LogisticAttributes || []),
  ];
});

const isSpecialAttributeName = (attribute: any) =>
  attribute?.TechnicalName && attribute.AttributeKey === 'SCHL_WEITE';

const getBlankUnit = (unit: any) => unit.toString().replace(/[[\]]/g, '');
</script>

<style src="./technical-details-table-fi-unscoped" />

<style scoped src="./technical-details-table-fi" />
